import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  HostListener,
  ViewChild,
  ElementRef,
  NgZone,
} from "@angular/core";
import { Router } from "@angular/router";
import { Title, Meta, DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { CmsserviceService } from "../../common-cms-service/cmsservice.service";
declare var $: any;

interface Slide {
  title: string;
  description: string;
  image: string;
  image_mobile?: string;
  button?: string;
  route?: string;
  tag?: string;
}

interface StudentStory {
  name: string;
  photo: string;
  initials: string;
  accent: "navy" | "cyan" | "gold";
  verified: boolean;
  story: string;
  intro: string;
  points: string[];
}

interface VideoItem {
  id: string;
  thumb: string;
  embed: SafeResourceUrl;
}

@Component({
  selector: "app-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.css"],
  standalone: false,
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();

  // 🔹 Dynamic banners
  slides: Slide[] = [];
  defaultHeroImage =
    "https://res.cloudinary.com/dlddixhyu/image/upload/f_auto,q_auto,w_1600/v1768075855/2150256499_yg6l6v.jpg";

  // 🔹 Shown only while the CMS banner call is loading / returns empty
  fallbackSlides: Slide[] = [
    {
      title:
        "Master Your GCSEs: Expert Tutoring For <span>AQA, Edexcel &amp; OCR</span>",
      description:
        "Tailored online lessons in Maths, English, and Sciences led by elite UK tutors. Boost grades and build exam confidence.",
      image:
        "https://res.cloudinary.com/dlddixhyu/image/upload/f_auto,q_auto,w_1600/v1768075855/2150256499_yg6l6v.jpg",
      button: "See More",
      route: "/",
    },
  ];

  // 🔹 Hero coverflow slider state
  @ViewChild("cfTrack") cfTrackRef: ElementRef;
  heroActiveIndex = 0;
  heroAutoplayId: any = null;
  heroAutoplayPaused = false;
  heroReducedMotion = false;
  heroScrollTicking = false;

  // 🔹 Other data
  title = "Sehyog";
  siteSetting: any;
  images: any;

  // 🔹 Student Corner swipe cards
  @ViewChild("scTrack") scTrackRef: ElementRef;
  scActiveIndex = 0;
  scScrollTicking = false;

  students: StudentStory[] = [
    {
      name: "",
      initials: "AK",
      photo:
        "https://res.cloudinary.com/do7r12azt/image/upload/v1788023466/Testimony-4_r8dm1g.png",
      accent: "navy",
      verified: true,
      intro: "Here's how Sehyog worked for him:",
      story:
        "After a focused 11+ journey with Sehyog Academy, he secured a place at QE Boys. Regular mock exams helped him identify and improve his weak areas, while crash courses, seminars, algebra courses and varied comprehension practice built his skills and confidence for the real exam.",
      points: [
        "Secured a Place at QE Boys",
        "Identified and Improved Weak Areas Through Mock Exams",
        "Built Skills and Confidence Through Targeted Courses",
        "Strengthened Comprehension With Varied Exam Practice",
      ],
    },
    {
      name: "",
      initials: "PM",
      photo:
        "https://res.cloudinary.com/do7r12azt/image/upload/v1788023464/Student_Testimony_2_akq464.png",
      accent: "gold",
      verified: true,
      intro: "Here's how Sehyog worked for him:",
      story:
        "Regular reading helped build vocabulary, comprehension and writing skills, while Sehyog Academy’s topic tests highlighted weak areas and focused revision. CSSE mock exams then provided valuable experience with challenging, exam-style questions.",
      points: [
        "Built Vocabulary Through Regular Reading",
        "Improved Comprehension and Writing Skills",
        "Identified Weak Areas With Topic Tests",
        "Gained Exam Experience Through CSSE Mocks",
      ],
    },
    {
      name: "",
      initials: "ZR",
      photo:
        "https://res.cloudinary.com/do7r12azt/image/upload/v1788023465/0829_aus99x.png",
      accent: "cyan",
      verified: true,
      intro: "Here's how Sehyog worked for him:",
      story:
        "After dedicated preparation with Sehyog Academy, he secured a place at Queen Elizabeth Boys’ School. Mock exams, detailed feedback, expert advice and crash courses helped him learn from his mistakes and approach the 11+ with greater confidence.",
      points: [
        "Secured a Place at Queen Elizabeth Boys’ School",
        "Learned From Mistakes Through Mock Exams",
        "Built Confidence With Expert Feedback and Advice",
        "Strengthened Preparation Through Crash Courses",
      ],
    },
    {
      name: "",
      initials: "DO",
      photo:
        "https://res.cloudinary.com/do7r12azt/image/upload/v1788023467/0829_1_pl3p5v.png",
      accent: "navy",
      verified: true,
      intro: "Here's how Sehyog worked for her:",
      story:
        "Sehyog’s mock exams gave him a clear understanding of the real 11+ exam, including question types, content and timings. Supportive teachers identified areas where he lacked confidence and helped him strengthen topics beyond what he had covered at school, making him more confident, independent and prepared.",
      points: [
        "Gained Real Exam Experience Through Mock Exams",
        "Identified and Improved Areas Lacking Confidence",
        "Strengthened Topics Beyond School Learning",
        "Became More Confident and Independent",
      ],
    },
  ];

  // 🔹 Video testimonials — thumbnails first, iframe only after click (saves ~5 YouTube players on load)
  videos = ["br6vkzqpZHY", "gLnO0THbHTg", "vEFQ9gK0cNY", "Q9O1heDQfHI", "bSFVlZahTac"];
  videoItems: VideoItem[] = [];
  playingVideoId: string = null;

  currentVSlide = 0;
  videosPerView = 3;
  totalSlides = 0;
  dots: number[] = [];

  // 🔹 Popup banners
  popups = [];
  currentPopup: any = null;
  popupTimeout: any = null;

  // 🔹 Native scroll listeners (registered outside Angular zone)
  private heroScrollFn: any = null;
  private scScrollFn: any = null;

  // Announcement ticker
  tickerVisible = true;

  constructor(
    private cmsService: CmsserviceService,
    private router: Router,
    private titleService: Title,
    private meta: Meta,
    private sanitizer: DomSanitizer,
    private ngZone: NgZone,
  ) {
    // updateTag (not addTags) so tags don't duplicate every time the page is opened
    this.meta.updateTag({ name: "description", content: "This is 11plus Examination portal" });
    this.meta.updateTag({
      name: "keywords",
      content:
        "gcse revision, gcse maths, gcse past papers, gcse science, gcse maths past papers, gcse revision websites, science gcse online, english gcse revision, mathematics gcse, science and maths tutor, online maths test, english language gcse revision, edexcel gcse maths, 11 plus exam papers, 11 plus tuition, 11 plus tutor, 11 plus tutors near me, 11 plus mock exams, 11 plus mock test, 11 plus papers, 11 plus test papers, eleven plus tuition, eleven plus mock exams, eleven plus papers, eleven plus tutors, 11 plus exam tuition, eleven plus exam papers, 11 plus exam tutors, 11 plus exam mock test, eleven plus test papers, eleven plus mock test, eleven plus verbal reasoning",
    });

    var self = this;

    // Request right-sized, compressed student photos from Cloudinary
    this.students.forEach(function (s) {
      s.photo = self.optimizeImg(s.photo, 800);
    });

    this.videoItems = this.videos.map(function (id) {
      return {
        id: id,
        thumb: "https://i.ytimg.com/vi/" + id + "/hqdefault.jpg",
        embed: self.sanitizer.bypassSecurityTrustResourceUrl(
          "https://www.youtube.com/embed/" + id + "?autoplay=1&rel=0",
        ),
      };
    });
  }

  ngOnInit() {
    this.titleService.setTitle(this.title);
    this.loadHomeBanner();

    this.cmsService
      .commonSiteSettingSingleton()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        this.siteSetting = res;
      });

    this.upcomingTest();
    this.schedulePopup();
    $(".loader-bg").fadeIn();
    this.calculateView();
  }

  ngAfterViewInit() {
    var self = this;
    this.heroReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    // Scroll + autoplay run outside Angular so each scroll frame doesn't trigger change detection
    this.ngZone.runOutsideAngular(function () {
      self.heroScrollFn = function () {
        self.onHeroScroll();
      };
      self.scScrollFn = function () {
        self.onScScroll();
      };
      if (self.cfTrackRef) {
        self.cfTrackRef.nativeElement.addEventListener("scroll", self.heroScrollFn, { passive: true });
      }
      if (self.scTrackRef) {
        self.scTrackRef.nativeElement.addEventListener("scroll", self.scScrollFn, { passive: true });
      }

      // Wait a tick so *ngFor has rendered the cards before measuring them
      setTimeout(function () {
        self.updateHeroScales();
        self.startHeroAutoplay();
        self.updateStudentScales();
      }, 0);
    });
  }

  /* ================= Images ================= */

  /** Add size + compression params for Cloudinary and Pexels URLs */
  optimizeImg(url: string, width: number): string {
    if (!url) {
      return url;
    }
    if (url.indexOf("res.cloudinary.com") > -1 && url.indexOf("/upload/") > -1 && url.indexOf("/upload/f_auto") === -1) {
      return url.replace("/upload/", "/upload/f_auto,q_auto,w_" + width + "/");
    }
    if (url.indexOf("images.pexels.com") > -1 && url.indexOf("?") === -1) {
      return url + "?auto=compress&cs=tinysrgb&w=" + width;
    }
    return url;
  }

  /**
   * Bound on every <img> via (error), so it works from the first paint.
   * Logs the failing URL (visible in the console), hides broken logos,
   * gives the hero its default image, and hides the broken icon elsewhere.
   */
  onImgError(event: any) {
    var img = event && event.target;
    if (!img || img.getAttribute("data-fb")) {
      return;
    }
    img.setAttribute("data-fb", "1");
    console.warn("[Home] Image failed to load:", img.currentSrc || img.src);

    if (img.closest(".sh-school-chip")) {
      img.style.display = "none";
      return;
    }
    if (img.closest(".sh-cf-card") && img.src !== this.defaultHeroImage) {
      img.src = this.defaultHeroImage;
      return;
    }
    img.classList.add("sh-img-missing");
  }

  /** Fetch banners dynamically from backend */
  loadHomeBanner() {
    this.cmsService
      .homeBanner()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          if (res && Array.isArray(res.data)) {
            this.slides = res.data.map((banner: any) => {
              let description = banner.description || "";
              let route = "/";

              const linkMatch = description.match(
                /link\s*=\s*(https?:\/\/[^\s"<]+)/i,
              );
              if (linkMatch && linkMatch[1]) {
                route = linkMatch[1];
                description = description.replace(linkMatch[0], "").trim();
              }

              return {
                title: banner.title || "",
                description,
                image: this.optimizeImg(banner.image || "", 1600),
                button: "See More",
                route,
              } as Slide;
            });

            // Re-measure hero cards once real slides replace the fallback
            var self = this;
            setTimeout(function () {
              self.heroActiveIndex = 0;
              self.updateHeroScales();
            }, 0);
          } else {
            console.warn("No banner data found:", res);
          }
        },
        error: (err) => {
          console.error("Error fetching home banners:", err);
        },
      });
  }

  /** Slides actually rendered in the hero — falls back to a default while CMS data loads */
  get heroSlides(): Slide[] {
    return this.slides && this.slides.length > 0
      ? this.slides
      : this.fallbackSlides;
  }

  /** Plain-text version of an HTML title (for alt text) */
  stripTags(html: string): string {
    return (html || "").replace(/<[^>]*>/g, "").replace(/&amp;/g, "&");
  }

  /* ================= Shared carousel helpers ================= */

  /** Scale/dim items by distance from the track centre; returns the closest index */
  private applyScales(track: any, selector: string, scaleK: number, opacityK: number): number {
    var items = track.querySelectorAll(selector);
    if (!items || items.length === 0) {
      return -1;
    }
    var trackRect = track.getBoundingClientRect();
    var trackCenter = trackRect.left + trackRect.width / 2;
    var closestIndex = 0;
    var closestDist = Infinity;

    for (var i = 0; i < items.length; i++) {
      var rect = items[i].getBoundingClientRect();
      var dist = Math.abs(rect.left + rect.width / 2 - trackCenter);
      var normalized = Math.min(dist / (trackRect.width / 2), 1);
      items[i].style.transform = "scale(" + (1 - normalized * scaleK).toFixed(3) + ")";
      items[i].style.opacity = (1 - normalized * opacityK).toFixed(3);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    }
    return closestIndex;
  }

  /** Scroll only the track (never scrollIntoView — avoids a vertical page jump) */
  private scrollTrackTo(track: any, selector: string, i: number) {
    var items = track.querySelectorAll(selector);
    if (!items || items.length === 0) {
      return;
    }
    var target = Math.max(0, Math.min(i, items.length - 1));
    var item = items[target];
    var trackRect = track.getBoundingClientRect();
    var itemRect = item.getBoundingClientRect();
    var delta =
      itemRect.left + itemRect.width / 2 - (trackRect.left + trackRect.width / 2);
    track.scrollTo({
      left: track.scrollLeft + delta,
      behavior: this.heroReducedMotion ? "auto" : "smooth",
    });
  }

  /* ================= Hero ================= */

  onHeroScroll() {
    var self = this;
    if (this.heroScrollTicking) {
      return;
    }
    this.heroScrollTicking = true;
    window.requestAnimationFrame(function () {
      self.updateHeroScales();
      self.heroScrollTicking = false;
    });
  }

  updateHeroScales() {
    var self = this;
    if (!this.cfTrackRef) {
      return;
    }
    var idx = this.applyScales(this.cfTrackRef.nativeElement, ".sh-cf-card", 0.12, 0.6);
    if (idx > -1 && idx !== this.heroActiveIndex) {
      // Re-enter Angular only when the active dot actually changes
      this.ngZone.run(function () {
        self.heroActiveIndex = idx;
      });
    }
  }

  scrollToHeroIndex(i: number) {
    if (!this.cfTrackRef) {
      return;
    }
    this.scrollTrackTo(this.cfTrackRef.nativeElement, ".sh-cf-card", i);
  }

  heroPrev() {
    this.scrollToHeroIndex(this.heroActiveIndex - 1);
  }
  heroNext() {
    this.scrollToHeroIndex(this.heroActiveIndex + 1);
  }
  goToHeroDot(i: number) {
    this.scrollToHeroIndex(i);
  }

  startHeroAutoplay() {
    var self = this;
    this.clearHeroAutoplay();
    if (this.heroReducedMotion) {
      return;
    }
    this.ngZone.runOutsideAngular(function () {
      self.heroAutoplayId = setInterval(function () {
        if (self.heroAutoplayPaused || document.hidden) {
          return;
        }
        var total = self.heroSlides.length;
        if (total < 2) {
          return;
        }
        self.scrollToHeroIndex((self.heroActiveIndex + 1) % total);
      }, 5500);
    });
  }

  clearHeroAutoplay() {
    if (this.heroAutoplayId) {
      clearInterval(this.heroAutoplayId);
      this.heroAutoplayId = null;
    }
  }

  pauseHeroAutoplay() {
    this.heroAutoplayPaused = true;
  }

  resumeHeroAutoplay() {
    var self = this;
    setTimeout(function () {
      self.heroAutoplayPaused = false;
    }, 600);
  }

  onSlideButtonClick(index: number) {
    const slide = this.heroSlides[index];
    if (slide && slide.route) {
      if (slide.route.startsWith("http")) {
        window.open(slide.route, "_blank");
      } else {
        this.router.navigate([slide.route]);
      }
    }
  }

  /* ================= Student Corner ================= */

  onScScroll() {
    var self = this;
    if (this.scScrollTicking) {
      return;
    }
    this.scScrollTicking = true;
    window.requestAnimationFrame(function () {
      self.updateStudentScales();
      self.scScrollTicking = false;
    });
  }

  updateStudentScales() {
    var self = this;
    if (!this.scTrackRef) {
      return;
    }
    var idx = this.applyScales(this.scTrackRef.nativeElement, ".sh-sc-slide", 0.08, 0.5);
    if (idx > -1 && idx !== this.scActiveIndex) {
      this.ngZone.run(function () {
        self.scActiveIndex = idx;
      });
    }
  }

  scrollToStudentIndex(i: number) {
    if (!this.scTrackRef) {
      return;
    }
    this.scrollTrackTo(this.scTrackRef.nativeElement, ".sh-sc-slide", i);
  }

  scPrev() {
    this.scrollToStudentIndex(this.scActiveIndex - 1);
  }
  scNext() {
    this.scrollToStudentIndex(this.scActiveIndex + 1);
  }
  goToScDot(i: number) {
    this.scrollToStudentIndex(i);
  }

  /* ================= Upcoming sessions ================= */

  upcomingTest() {
    var self = this;
    this.cmsService
      .upcomingEventsRecord()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        var list = res && res.data ? res.data : [];
        list.forEach(function (item: any) {
          if (item && item.test_image) {
            item.test_image = self.optimizeImg(item.test_image, 600);
          }
        });
        this.images = list;
      });
  }

  /* ================= Popup ================= */

  schedulePopup() {
    this.popupTimeout = setTimeout(() => this.showRandomPopup(), 25000);
  }

  showRandomPopup() {
    if (!this.popups.length) {
      return;
    }
    const idx = Math.floor(Math.random() * this.popups.length);
    this.currentPopup = this.popups[idx];
  }

  closePopup() {
    this.currentPopup = null;
    this.schedulePopup();
  }

  /* ================= Video testimonials ================= */

  playVideo(id: string) {
    this.playingVideoId = id;
  }

  get transformStyle(): string {
    return `translateX(${-this.currentVSlide * 100}%)`;
  }

  slideVideo(direction: number) {
    this.currentVSlide = Math.max(
      0,
      Math.min(this.currentVSlide + direction, this.totalSlides - 1),
    );
  }

  goToSlide(index: number) {
    this.currentVSlide = index;
  }

  calculateView() {
    const width = window.innerWidth;
    this.videosPerView = width <= 640 ? 1 : width <= 992 ? 2 : 3;
    this.totalSlides = Math.ceil(this.videos.length / this.videosPerView);
    this.currentVSlide = Math.min(this.currentVSlide, this.totalSlides - 1);
    this.dots = Array(this.totalSlides).fill(0);
    this.updateHeroScales();
    this.updateStudentScales();
  }

  @HostListener("window:resize")
  onResize() {
    this.calculateView();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    if (this.popupTimeout) clearTimeout(this.popupTimeout);
    this.clearHeroAutoplay();
    if (this.cfTrackRef && this.heroScrollFn) {
      this.cfTrackRef.nativeElement.removeEventListener("scroll", this.heroScrollFn);
    }
    if (this.scTrackRef && this.scScrollFn) {
      this.scTrackRef.nativeElement.removeEventListener("scroll", this.scScrollFn);
    }
  }
}
